<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
import {
  ref,
  computed,
  nextTick,
  onMounted,
  onBeforeUnmount,
  watch,
} from 'vue';
import camelcaseKeys from 'camelcase-keys';
import { useRoute, useRouter } from 'vue-router';
import LaisDisparosAPI from 'dashboard/api/laisDisparos';
import ImovelAPI from 'dashboard/api/imoveis';
import { useAlert } from 'dashboard/composables';
import { useEscClose } from 'dashboard/composables/useEscClose';
import LaisHeader from 'dashboard/components-next/LaisHeader.vue';
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import LaisField from 'dashboard/components-next/LaisField.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import LaisConfirmModal from 'dashboard/components-next/LaisConfirmModal.vue';
import LaisModal from 'dashboard/components-next/LaisModal.vue';
import {
  ATUALIZACAO_TEMPLATE_CATEGORIES,
  ATUALIZACAO_TEMPLATE_LIBRARY,
  atualizacaoTemplatesByCategory,
  buildTemplateMetadataPayload,
  findAtualizacaoTemplate,
  renderTemplatePreview,
  templateReviewDiffFromDisparo,
  templateUsageFromDisparo,
  templateVariableDefinitions,
  validateTemplateMessage,
} from '../helpers/atualizacaoTemplateLibrary';

const route = useRoute();
const router = useRouter();
const routeString = value => (Array.isArray(value) ? value[0] : value);
const normalizedTab = value =>
  ['em_revisao', 'agendados', 'historico'].includes(value)
    ? value
    : 'em_revisao';

const tab = ref(normalizedTab(routeString(route.query.tab)));
const disparos = ref([]);
const isLoading = ref(false);
const isSaving = ref(false);
const showModal = ref(false);
const previewCount = ref(null);
const showCancelModal = ref(false);
const cancelTarget = ref(null);
const isCancelling = ref(false);
const reviewingDisparoId = ref(null);
const inactiveListings = ref([]);
const isLoadingListings = ref(false);
const showRepublishModal = ref(false);
const selectedListing = ref(null);
const isReactivatingListing = ref(false);
const republishFeedback = ref(null);

const DEFAULT_TEMPLATE = ATUALIZACAO_TEMPLATE_LIBRARY[0];
const selectedTemplateCategoryId = ref(
  DEFAULT_TEMPLATE?.categoryId || ATUALIZACAO_TEMPLATE_CATEGORIES[0]?.id || ''
);

const buildDefaultNovoDisparo = () => ({
  titulo: '',
  canal: 'whatsapp',
  filtro_tipo: '',
  filtro_bairro: '',
  filtro_finalidade: '',
  agendado_para: '',
  mensagem: DEFAULT_TEMPLATE?.body || '',
  status: 'em_revisao',
  template_id: DEFAULT_TEMPLATE?.id || '',
});

const novoDisparo = ref(buildDefaultNovoDisparo());

const STATUS = {
  em_revisao: { label: 'Em revisão', cls: 'text-lais-primary bg-[#f5eef9]' },
  aprovado: { label: 'Aprovado', cls: 'text-emerald-700 bg-emerald-50' },
  agendado: { label: 'Agendado', cls: 'text-sky-700 bg-sky-50' },
  enviando: { label: 'Em disparo', cls: 'text-amber-700 bg-amber-50' },
  enviado: { label: 'Realizado', cls: 'text-emerald-700 bg-emerald-50' },
  cancelado: { label: 'Cancelado', cls: 'text-rose-700 bg-rose-50' },
  reprovado: { label: 'Reprovado', cls: 'text-rose-700 bg-rose-50' },
  erro: { label: 'Falhou', cls: 'text-rose-700 bg-rose-50' },
};

const fmtDate = iso => {
  if (!iso) return '—';
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(iso));
};

const input =
  'w-full px-3 py-2.5 text-[14px] text-[#1a1a1a] bg-white border border-[#e5e7eb] rounded-lais-sm focus:outline-none focus:border-lais-primary focus:ring-2 focus:ring-[#f5eef9] placeholder:text-lais-text-muted transition-all';

const MIN_REMARKETING_CONTACTS = 30;
const MAX_REMARKETING_CONTACTS = 60;
const SCHEDULE_RESERVING_STATUSES = [
  'em_revisao',
  'aprovado',
  'agendado',
  'enviando',
];

const REVIEW_ACTION_COPY = {
  reject: {
    title: 'Reprovar disparo',
    confirmText: 'Reprovar',
    placeholder: 'Descreva o motivo da reprovação para a operação.',
    fallback: 'Reprovado na revisão.',
    success: 'Disparo reprovado.',
    error: 'Não foi possível reprovar o disparo.',
    confirmClass: 'bg-[#b42318] text-white hover:bg-[#9f1f15]',
  },
  request_changes: {
    title: 'Solicitar ajuste',
    confirmText: 'Solicitar ajuste',
    placeholder: 'Descreva o ajuste necessário antes da aprovação.',
    fallback: 'Ajustes solicitados.',
    success: 'Ajuste registrado.',
    error: 'Não foi possível solicitar ajuste.',
    confirmClass: 'bg-lais-primary text-white hover:bg-[#3d2868]',
  },
};

const scheduleDayKey = value => {
  if (!value) return '';

  const rawValue = String(value);
  if (/^\d{4}-\d{2}-\d{2}/.test(rawValue)) return rawValue.slice(0, 10);

  const parsedDate = new Date(rawValue);
  if (Number.isNaN(parsedDate.getTime())) return '';

  return parsedDate.toISOString().slice(0, 10);
};

const statusMatchesTab = disparo => {
  if (tab.value === 'em_revisao') return disparo.status === 'em_revisao';
  if (tab.value === 'agendados') return disparo.status === 'agendado';

  return !['em_revisao', 'agendado'].includes(disparo.status);
};

const emptyTabLabel = computed(() => {
  if (tab.value === 'em_revisao') return 'em revisão';
  if (tab.value === 'agendados') return 'agendado';

  return 'no histórico';
});

const filtrados = computed(() => disparos.value.filter(statusMatchesTab));

const compactQuery = query =>
  Object.fromEntries(
    Object.entries(query).filter(([, value]) => value != null && value !== '')
  );

const setTab = nextTab => {
  tab.value = normalizedTab(nextTab);
  router
    .replace({
      query: compactQuery({
        ...route.query,
        tab: tab.value === 'em_revisao' ? undefined : tab.value,
      }),
    })
    .catch(() => {});
};
const finalidadePreviewLabel = value => {
  if (value === 'venda') return 'compra';
  if (value === 'aluguel') return 'aluguel';

  return 'compra ou aluguel';
};
const selectedTemplate = computed(() =>
  findAtualizacaoTemplate(novoDisparo.value.template_id)
);
const selectedTemplateCategory = computed(() =>
  ATUALIZACAO_TEMPLATE_CATEGORIES.find(
    category => category.id === selectedTemplate.value?.categoryId
  )
);
const templatesForSelectedCategory = computed(() =>
  atualizacaoTemplatesByCategory(selectedTemplateCategoryId.value)
);
const selectedTemplateVariableDefinitions = computed(() =>
  templateVariableDefinitions(selectedTemplate.value)
);
const templatePreviewValues = computed(() => ({
  bairro: novoDisparo.value.filtro_bairro,
  tipo_imovel: novoDisparo.value.filtro_tipo,
  finalidade: finalidadePreviewLabel(novoDisparo.value.filtro_finalidade),
}));
const templatePreviewText = computed(() =>
  renderTemplatePreview(novoDisparo.value.mensagem, templatePreviewValues.value)
);
const templateValidationIssues = computed(() =>
  validateTemplateMessage(selectedTemplate.value, novoDisparo.value.mensagem)
);
const templateValidationClass = computed(() =>
  templateValidationIssues.value.length
    ? 'border-rose-200 bg-rose-50 text-[#b42318]'
    : 'border-emerald-200 bg-emerald-50 text-emerald-800'
);
const templateUsageForDisparo = disparo => templateUsageFromDisparo(disparo);
const reviewDiffForDisparo = disparo => templateReviewDiffFromDisparo(disparo);
const cancelDescription = computed(() =>
  cancelTarget.value?.titulo
    ? `Deseja cancelar o disparo "${cancelTarget.value.titulo}"?`
    : 'Deseja cancelar o disparo selecionado?'
);
const selectedListingBusinessContext = computed(() =>
  selectedListing.value?.finalidade === 'venda' ? 'SALE' : 'RENT'
);
const selectedListingBusinessLabel = computed(() =>
  selectedListingBusinessContext.value === 'SALE' ? 'venda' : 'aluguel'
);
const republishTitle = computed(
  () => `Deseja reativar o anúncio para ${selectedListingBusinessLabel.value}?`
);
const hasInactiveListings = computed(() => inactiveListings.value.length > 0);
const numericPreviewCount = computed(() => Number(previewCount.value));
const hasAudiencePreview = computed(
  () =>
    previewCount.value !== null &&
    previewCount.value !== undefined &&
    Number.isFinite(numericPreviewCount.value)
);
const isAudienceBelowMinimum = computed(
  () =>
    hasAudiencePreview.value &&
    numericPreviewCount.value < MIN_REMARKETING_CONTACTS
);
const isAudienceAboveMaximum = computed(
  () =>
    hasAudiencePreview.value &&
    numericPreviewCount.value > MAX_REMARKETING_CONTACTS
);
const isAudienceOutOfRange = computed(
  () => isAudienceBelowMinimum.value || isAudienceAboveMaximum.value
);
const isAudienceInRange = computed(
  () => hasAudiencePreview.value && !isAudienceOutOfRange.value
);
const audienceRangeMessage = computed(() => {
  if (!hasAudiencePreview.value) {
    return 'A Lais validará a audiência antes da revisão. O público deve ficar entre 30 e 60 contatos qualificados.';
  }
  if (isAudienceBelowMinimum.value) {
    return `O público atual tem ${numericPreviewCount.value} contatos. Ajuste os filtros para chegar a pelo menos 30.`;
  }
  if (isAudienceAboveMaximum.value) {
    return `O público atual tem ${numericPreviewCount.value} contatos. Refine os filtros para ficar em no máximo 60.`;
  }

  return `Público dentro da regra: ${numericPreviewCount.value} contatos.`;
});
const audienceRangeClass = computed(() =>
  isAudienceInRange.value
    ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
    : 'border-amber-200 bg-amber-50 text-[#7a4b00]'
);
const selectedScheduleDay = computed(() =>
  scheduleDayKey(novoDisparo.value.agendado_para)
);
const hasDailyDisparoConflict = computed(() => {
  if (!selectedScheduleDay.value) return false;

  return disparos.value.some(disparo => {
    if (!SCHEDULE_RESERVING_STATUSES.includes(disparo.status)) return false;

    return scheduleDayKey(disparo.agendado_para) === selectedScheduleDay.value;
  });
});
const dailyLimitMessage = computed(() => {
  if (hasDailyDisparoConflict.value) {
    return 'Já existe um disparo reservado para essa data. Escolha outro dia para enviar à revisão.';
  }
  if (!selectedScheduleDay.value) {
    return 'Ao escolher a data, a Lais bloqueia o envio se já houver outro disparo reservado no mesmo dia.';
  }

  return 'Regra de cadência: no máximo 1 disparo de remarketing por dia.';
});
const novoDisparoSubmitDisabled = computed(
  () =>
    isSaving.value ||
    templateValidationIssues.value.length > 0 ||
    isAudienceOutOfRange.value ||
    hasDailyDisparoConflict.value
);
const reviewModalTarget = ref(null);
const reviewModalKind = ref('reject');
const reviewNotes = ref('');
const reviewNotesTextarea = ref(null);
const isReviewModalOpen = computed(() => !!reviewModalTarget.value);
const reviewModalConfig = computed(
  () => REVIEW_ACTION_COPY[reviewModalKind.value] || REVIEW_ACTION_COPY.reject
);
const isReviewModalSubmitting = computed(
  () => reviewingDisparoId.value === reviewModalTarget.value?.id
);
let previewTimer = null;

const clearPreviewTimer = () => {
  if (previewTimer) {
    clearTimeout(previewTimer);
    previewTimer = null;
  }
};

const aplicarTemplateAoDisparo = template => {
  if (!template) return;

  selectedTemplateCategoryId.value = template.categoryId;
  novoDisparo.value = {
    ...novoDisparo.value,
    titulo: template.defaultTitle,
    canal: template.channel || novoDisparo.value.canal || 'whatsapp',
    mensagem: template.body,
    template_id: template.id,
  };
};

const selecionarCategoriaTemplate = categoryId => {
  selectedTemplateCategoryId.value = categoryId;
  aplicarTemplateAoDisparo(atualizacaoTemplatesByCategory(categoryId)[0]);
};

const selecionarTemplate = templateId => {
  aplicarTemplateAoDisparo(findAtualizacaoTemplate(templateId));
};

const resetNovoDisparo = () => {
  selectedTemplateCategoryId.value =
    DEFAULT_TEMPLATE?.categoryId ||
    ATUALIZACAO_TEMPLATE_CATEGORIES[0]?.id ||
    '';
  novoDisparo.value = buildDefaultNovoDisparo();
  previewCount.value = null;
};

const buildNovoDisparoPayload = () => ({
  titulo: novoDisparo.value.titulo,
  canal: novoDisparo.value.canal,
  filtro_tipo: novoDisparo.value.filtro_tipo,
  filtro_bairro: novoDisparo.value.filtro_bairro,
  filtro_finalidade: novoDisparo.value.filtro_finalidade,
  agendado_para: novoDisparo.value.agendado_para,
  mensagem: novoDisparo.value.mensagem,
  status: novoDisparo.value.status,
  filtros: {
    ...(novoDisparo.value.filtros || {}),
    template_metadata: buildTemplateMetadataPayload({
      template: selectedTemplate.value,
      finalMessage: novoDisparo.value.mensagem,
    }),
  },
});

const carregar = async () => {
  isLoading.value = true;
  try {
    const { data } = await LaisDisparosAPI.get();
    disparos.value = data.payload || data.data || [];
  } catch (e) {
    disparos.value = [];
  } finally {
    isLoading.value = false;
  }
};

const carregarAnunciosPausados = async () => {
  isLoadingListings.value = true;
  try {
    const { data } = await ImovelAPI.get({
      status: 'inativo',
      per_page: 6,
      page: 1,
    });
    inactiveListings.value = camelcaseKeys(data.payload || [], { deep: true });
  } catch (e) {
    inactiveListings.value = [];
  } finally {
    isLoadingListings.value = false;
  }
};

const abrirNovo = async () => {
  showModal.value = true;
  clearPreviewTimer();
  previewTimer = setTimeout(async () => {
    try {
      const { data } = await LaisDisparosAPI.preview({
        lais_disparo: {
          canal: novoDisparo.value.canal,
          filtro_tipo: novoDisparo.value.filtro_tipo,
          filtro_bairro: novoDisparo.value.filtro_bairro,
          filtro_finalidade: novoDisparo.value.filtro_finalidade,
          qualificados_apenas: true,
        },
      });
      previewCount.value = data.leads_estimados;
    } catch (e) {
      previewCount.value = null;
    }
  }, 0);
};

const fecharNovo = () => {
  clearPreviewTimer();
  showModal.value = false;
};

useEscClose(showModal, fecharNovo);
useEscClose(showRepublishModal, () => {
  showRepublishModal.value = false;
});
useEscClose(showCancelModal, () => {
  showCancelModal.value = false;
  cancelTarget.value = null;
});

const abrirRepublish = listing => {
  selectedListing.value = listing;
  republishFeedback.value = null;
  showRepublishModal.value = true;
};

const fecharRepublish = () => {
  showRepublishModal.value = false;
  selectedListing.value = null;
  republishFeedback.value = null;
};

const reativarAnuncio = async () => {
  if (!selectedListing.value?.id) return;

  isReactivatingListing.value = true;
  republishFeedback.value = null;
  try {
    const { data } = await ImovelAPI.reactivate(selectedListing.value.id, {
      business_context: selectedListingBusinessContext.value,
    });
    const message =
      data.message ||
      `Anúncio de ${selectedListingBusinessLabel.value} publicado com sucesso.`;
    republishFeedback.value = {
      kind: 'positive',
      title: message,
      subtitle: null,
    };
    useAlert(message);
    inactiveListings.value = inactiveListings.value.filter(
      listing => listing.id !== selectedListing.value.id
    );
  } catch (e) {
    const title =
      e.response?.data?.title ||
      `Não foi possível reativar o anúncio para ${selectedListingBusinessLabel.value}`;
    const subtitle =
      e.response?.data?.message || 'Por favor, entre em contato com o suporte.';
    republishFeedback.value = {
      kind: 'critical',
      title,
      subtitle,
    };
  } finally {
    isReactivatingListing.value = false;
  }
};

const atualizarPreview = async () => {
  try {
    const { data } = await LaisDisparosAPI.preview({
      lais_disparo: {
        canal: novoDisparo.value.canal,
        filtro_tipo: novoDisparo.value.filtro_tipo,
        filtro_bairro: novoDisparo.value.filtro_bairro,
        filtro_finalidade: novoDisparo.value.filtro_finalidade,
        qualificados_apenas: true,
      },
    });
    previewCount.value = data.leads_estimados;
  } catch (e) {
    previewCount.value = null;
  }
};

const agendarPreview = () => {
  clearPreviewTimer();
  previewTimer = setTimeout(atualizarPreview, 250);
};

const apiErrorMessage = (error, fallback) => {
  const errors = error.response?.data?.errors;
  if (Array.isArray(errors) && errors.length) return errors.join(', ');

  return (
    error.response?.data?.error || error.response?.data?.message || fallback
  );
};

const salvarDisparo = async () => {
  if (!novoDisparo.value.titulo) {
    useAlert('Informe um título para o disparo');
    return;
  }
  if (templateValidationIssues.value.length) {
    useAlert(templateValidationIssues.value[0].message);
    return;
  }
  if (isAudienceOutOfRange.value) {
    useAlert(audienceRangeMessage.value);
    return;
  }
  if (hasDailyDisparoConflict.value) {
    useAlert(dailyLimitMessage.value);
    return;
  }

  isSaving.value = true;
  try {
    await LaisDisparosAPI.create({
      lais_disparo: buildNovoDisparoPayload(),
    });
    useAlert('Solicitação enviada para revisão');
    fecharNovo();
    resetNovoDisparo();
    await carregar();
  } catch (e) {
    useAlert(apiErrorMessage(e, 'Não foi possível agendar o disparo'));
  } finally {
    isSaving.value = false;
  }
};

const validationIssues = disparo => [
  ...(disparo.validation_result?.errors || []),
  ...(disparo.validation_result?.warnings || []),
];

const errorReason = disparo => {
  const deliveryFailure = Array(disparo.resultado?.delivery_log).find(
    entry => entry.status === 'failed'
  );
  const validationIssue = validationIssues(disparo).find(
    issue => issue.message
  );

  return (
    disparo.erro_detalhe ||
    deliveryFailure?.detail ||
    validationIssue?.message ||
    'Falha no envio. Tente reenviar ou contate o suporte.'
  );
};

const abrirReviewModal = async (disparo, kind) => {
  reviewModalTarget.value = disparo;
  reviewModalKind.value = kind;
  reviewNotes.value = '';
  await nextTick();
  reviewNotesTextarea.value?.focus();
};

const resetReviewModal = () => {
  reviewModalTarget.value = null;
  reviewNotes.value = '';
  reviewModalKind.value = 'reject';
};

const fecharReviewModal = () => {
  if (isReviewModalSubmitting.value) return;

  resetReviewModal();
};

const reprovarDisparo = disparo => abrirReviewModal(disparo, 'reject');

const solicitarAjusteDisparo = disparo =>
  abrirReviewModal(disparo, 'request_changes');

const confirmarReviewModal = async () => {
  if (!reviewModalTarget.value?.id) return;

  const disparo = reviewModalTarget.value;
  const config = reviewModalConfig.value;
  const notes = reviewNotes.value.trim() || config.fallback;
  reviewingDisparoId.value = disparo.id;

  try {
    if (reviewModalKind.value === 'request_changes') {
      await LaisDisparosAPI.requestChanges(disparo.id, { review_notes: notes });
    } else {
      await LaisDisparosAPI.reject(disparo.id, { review_notes: notes });
    }
    useAlert(config.success);
    resetReviewModal();
    await carregar();
  } catch (e) {
    useAlert(apiErrorMessage(e, config.error));
  } finally {
    reviewingDisparoId.value = null;
  }
};

const validarDisparo = async disparo => {
  reviewingDisparoId.value = disparo.id;
  try {
    await LaisDisparosAPI.validate(disparo.id);
    useAlert('Validações atualizadas.');
    await carregar();
  } catch (e) {
    useAlert('Não foi possível validar o disparo.');
  } finally {
    reviewingDisparoId.value = null;
  }
};

const aprovarDisparo = async disparo => {
  reviewingDisparoId.value = disparo.id;
  try {
    await LaisDisparosAPI.approve(disparo.id, {
      review_notes: 'Aprovado para envio.',
    });
    useAlert('Disparo aprovado e agendado.');
    await carregar();
  } catch (e) {
    useAlert(
      e.response?.data?.errors?.join(', ') ||
        'Não foi possível aprovar o disparo.'
    );
  } finally {
    reviewingDisparoId.value = null;
  }
};

const solicitarCancelamento = disparo => {
  cancelTarget.value = disparo;
  showCancelModal.value = true;
};

const fecharCancelamento = () => {
  showCancelModal.value = false;
  cancelTarget.value = null;
};

const cancelarDisparo = async () => {
  if (!cancelTarget.value?.id) return;
  isCancelling.value = true;
  try {
    await LaisDisparosAPI.delete(cancelTarget.value.id);
    useAlert('Disparo cancelado');
    fecharCancelamento();
    await carregar();
  } catch (e) {
    useAlert('Não foi possível cancelar');
  } finally {
    isCancelling.value = false;
  }
};

watch(
  () => [
    showModal.value,
    novoDisparo.value.canal,
    novoDisparo.value.filtro_tipo,
    novoDisparo.value.filtro_bairro,
    novoDisparo.value.filtro_finalidade,
  ],
  ([isOpen]) => {
    if (!isOpen) return;
    agendarPreview();
  }
);

onMounted(() => {
  carregar();
  carregarAnunciosPausados();
});
onBeforeUnmount(clearPreviewTimer);
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <section
    class="flex flex-col h-full bg-white font-redHatText overflow-hidden"
  >
    <LaisHeader
      title="Atualização"
      helper="Avise clientes sobre novos imóveis, mudanças de preço ou disponibilidade. A Lais dispara via WhatsApp respeitando a janela de 24h."
    >
      <template #actions>
        <LaisPrimaryButton
          variant="primary"
          icon="i-lucide-plus"
          @click="abrirNovo"
        >
          Novo disparo
        </LaisPrimaryButton>
      </template>
    </LaisHeader>

    <div class="border-b border-[#e5e7eb] px-4 sm:px-6 lg:px-10">
      <div class="flex items-center gap-6">
        <button
          v-for="t in [
            { id: 'em_revisao', label: 'Em revisão' },
            { id: 'agendados', label: 'Agendados' },
            { id: 'historico', label: 'Histórico' },
          ]"
          :key="t.id"
          type="button"
          class="relative py-3 text-[14px] font-semibold transition-colors"
          :class="
            tab === t.id
              ? 'text-lais-primary'
              : 'text-lais-text-muted hover:text-lais-text-body'
          "
          @click="setTab(t.id)"
        >
          {{ t.label }}
          <span
            v-if="tab === t.id"
            class="absolute bottom-0 left-0 right-0 h-[3px] bg-lais-primary rounded-t"
          />
        </button>
      </div>
    </div>

    <div class="flex-1 overflow-auto p-4 sm:p-6 lg:p-10">
      <div class="grid w-full gap-5">
        <LaisCard title="Reativar anúncios pausados">
          <div class="flex flex-col gap-4">
            <div
              class="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between"
            >
              <p
                class="max-w-[720px] text-[13px] leading-relaxed text-lais-text-body"
              >
                Você poderá editar as características do anúncio após a
                republicação.
              </p>
              <button
                type="button"
                class="inline-flex items-center gap-2 text-[12px] font-semibold text-lais-primary hover:underline"
                @click="carregarAnunciosPausados"
              >
                <span class="i-lucide-refresh-cw size-3.5" aria-hidden="true" />
                Atualizar lista
              </button>
            </div>

            <div
              v-if="isLoadingListings"
              class="rounded-lais-sm border border-lais-border bg-lais-bg-soft px-4 py-6 text-center text-[13px] text-lais-text-muted"
            >
              Carregando anúncios pausados…
            </div>

            <div
              v-else-if="!hasInactiveListings"
              class="rounded-lais-sm border border-lais-border bg-lais-bg-soft px-4 py-6 text-center text-[13px] text-lais-text-muted"
            >
              Nenhum anúncio pausado para reativar agora.
            </div>

            <ul v-else class="grid gap-3">
              <li
                v-for="listing in inactiveListings"
                :key="listing.id"
                class="flex flex-col gap-3 rounded-lais-sm border border-lais-border bg-white p-4 shadow-lais-card sm:flex-row sm:items-center sm:justify-between"
              >
                <div class="min-w-0">
                  <p class="truncate text-[14px] font-semibold text-lais-text">
                    {{ listing.titulo }}
                  </p>
                  <p class="mt-1 text-[12px] text-lais-text-muted">
                    {{ listing.bairro || 'Bairro não informado' }}
                    <span v-if="listing.cidade">· {{ listing.cidade }}</span>
                    <span class="mx-1">·</span>
                    {{ listing.finalidade === 'venda' ? 'Venda' : 'Aluguel' }}
                  </p>
                </div>
                <LaisPrimaryButton
                  variant="primary"
                  icon="i-lucide-refresh-cw"
                  @click="abrirRepublish(listing)"
                >
                  Reativar anúncio
                </LaisPrimaryButton>
              </li>
            </ul>
          </div>
        </LaisCard>

        <div
          v-if="isLoading"
          class="text-center py-16 text-[13px] text-lais-text-muted"
        >
          Carregando…
        </div>
        <ul v-else class="grid gap-3">
          <li
            v-for="d in filtrados"
            :key="d.id"
            class="flex items-start gap-4 p-5 bg-white border border-[#e5e7eb] rounded-lais shadow-lais-card"
          >
            <div
              class="size-10 rounded-lais bg-[#f0e8fa] grid place-items-center shrink-0"
            >
              <span
                class="i-lucide-megaphone size-5 text-lais-primary"
                aria-hidden="true"
              />
            </div>
            <div class="flex-1 min-w-0">
              <h3 class="text-[14px] font-semibold text-[#1a1a1a]">
                {{ d.titulo }}
              </h3>
              <p class="text-[12px] text-lais-text-muted mt-1">
                <span
                  class="i-lucide-calendar size-3 inline-block align-text-top mr-1"
                />
                {{ fmtDate(d.agendado_para) }}
                <span class="mx-1.5">·</span>
                <strong class="text-[#1a1a1a]">{{
                  d.leads_atingidos || 0
                }}</strong>
                clientes alcançados
              </p>
              <div
                v-if="templateUsageForDisparo(d)"
                class="mt-3 rounded-lais-sm border border-lais-border bg-lais-bg-soft px-3 py-2 text-[12px] text-lais-text-body"
              >
                <p class="font-semibold text-lais-text">Template usado</p>
                <p class="mt-1">
                  {{ templateUsageForDisparo(d).label }}
                  <span v-if="templateUsageForDisparo(d).approvedVersion">
                    · v{{ templateUsageForDisparo(d).approvedVersion }}
                  </span>
                  <span v-if="templateUsageForDisparo(d).categoryLabel">
                    · {{ templateUsageForDisparo(d).categoryLabel }}
                  </span>
                </p>
              </div>
              <p
                v-if="d.status === 'enviado'"
                class="text-[12px] text-[#178565] mt-1"
              >
                ✓ {{ d.leads_atingidos || 0 }} entregues ·
                {{ d.respostas }} respostas
              </p>
              <div
                v-if="d.status === 'erro'"
                role="alert"
                class="mt-3 rounded-lais-sm border border-rose-200 bg-rose-50 px-3 py-2 text-[12px] text-[#b42318]"
              >
                <p class="font-semibold">Falha no envio</p>
                <p class="mt-1 leading-relaxed">
                  {{ errorReason(d) }}
                </p>
              </div>
              <div
                v-if="validationIssues(d).length"
                class="mt-3 grid gap-1 rounded-lais-sm bg-[#faf8fd] px-3 py-2"
              >
                <p
                  v-for="issue in validationIssues(d).slice(0, 3)"
                  :key="`${issue.code}-${issue.message}`"
                  class="text-[12px]"
                  :class="
                    issue.severity === 'warning'
                      ? 'text-[#9a5b13]'
                      : 'text-[#b42318]'
                  "
                >
                  {{ issue.message }}
                </p>
              </div>
              <div
                v-if="
                  d.status === 'em_revisao' && reviewDiffForDisparo(d).length
                "
                class="mt-3 grid gap-2 rounded-lais-sm border border-[#eadff5] bg-[#fbf8fe] px-3 py-2 text-[12px]"
              >
                <p class="font-semibold text-lais-primary">
                  Diff template vs texto final
                </p>
                <div
                  v-for="line in reviewDiffForDisparo(d)"
                  :key="line.key"
                  class="grid gap-1 rounded-lais-sm border px-3 py-2"
                  :class="
                    line.kind === 'unchanged'
                      ? 'border-lais-border bg-white text-lais-text-body'
                      : 'border-amber-200 bg-amber-50 text-[#7a4b00]'
                  "
                >
                  <p>
                    <span class="font-semibold">Template:</span>
                    {{ line.template || '—' }}
                  </p>
                  <p>
                    <span class="font-semibold">Final:</span>
                    {{ line.final || '—' }}
                  </p>
                </div>
              </div>
              <p v-if="d.review_notes" class="mt-2 text-[12px] text-[#6b7280]">
                Revisão: {{ d.review_notes }}
              </p>
              <p v-if="d.notified_at" class="mt-1 text-[12px] text-[#178565]">
                Imobiliária notificada em {{ fmtDate(d.notified_at) }}
              </p>
            </div>
            <div class="flex flex-col items-end gap-2">
              <span
                class="text-[11px] font-semibold uppercase tracking-wide px-2.5 py-1 rounded-full"
                :class="STATUS[d.status]?.cls"
              >
                {{ STATUS[d.status]?.label || d.status }}
              </span>
              <div v-if="d.status === 'em_revisao'" class="flex flex-col gap-1">
                <button
                  type="button"
                  class="text-[12px] text-lais-primary hover:underline disabled:opacity-60"
                  :disabled="reviewingDisparoId === d.id"
                  @click="validarDisparo(d)"
                >
                  Validar
                </button>
                <button
                  type="button"
                  class="text-[12px] text-[#178565] hover:underline disabled:opacity-60"
                  :disabled="reviewingDisparoId === d.id"
                  @click="aprovarDisparo(d)"
                >
                  Aprovar
                </button>
                <button
                  type="button"
                  class="text-[12px] text-[#9a5b13] hover:underline disabled:opacity-60"
                  :disabled="reviewingDisparoId === d.id"
                  @click="solicitarAjusteDisparo(d)"
                >
                  Solicitar ajuste
                </button>
                <button
                  type="button"
                  class="text-[12px] text-[#b42318] hover:underline disabled:opacity-60"
                  :disabled="reviewingDisparoId === d.id"
                  @click="reprovarDisparo(d)"
                >
                  Reprovar
                </button>
              </div>
              <button
                v-if="d.status === 'agendado'"
                type="button"
                class="text-[12px] text-[#944f7a] hover:underline"
                @click="solicitarCancelamento(d)"
              >
                Cancelar
              </button>
            </div>
          </li>
          <li
            v-if="filtrados.length === 0"
            class="text-center py-16 text-[13px] text-lais-text-muted"
          >
            <span
              class="i-lucide-megaphone size-8 inline-block opacity-40 mb-2"
            />
            <p>Nenhum disparo {{ emptyTabLabel }}.</p>
          </li>
        </ul>

        <LaisCard title="Como funciona">
          <ol class="flex flex-col gap-3 text-[13px] text-lais-text-body">
            <li class="flex gap-3">
              <span
                class="size-6 shrink-0 rounded-full bg-[#f0e8fa] text-lais-primary grid place-items-center text-[12px] font-semibold"
              >
                1
              </span>
              <span>
                Selecione um
                <strong class="text-[#1a1a1a]">filtro de imóveis</strong>
                (bairro, tipo, faixa de preço).
              </span>
            </li>
            <li class="flex gap-3">
              <span
                class="size-6 shrink-0 rounded-full bg-[#f0e8fa] text-lais-primary grid place-items-center text-[12px] font-semibold"
              >
                2
              </span>
              <span>
                A Lais identifica clientes que demonstraram
                <strong class="text-[#1a1a1a]">interesse compatível</strong>
                nos últimos 90 dias.
              </span>
            </li>
            <li class="flex gap-3">
              <span
                class="size-6 shrink-0 rounded-full bg-[#f0e8fa] text-lais-primary grid place-items-center text-[12px] font-semibold"
              >
                3
              </span>
              <span>
                A solicitação entra em revisão e só é enviada
                <strong class="text-[#1a1a1a]">após aprovação</strong>.
              </span>
            </li>
            <li class="flex gap-3">
              <span
                class="size-6 shrink-0 rounded-full bg-[#f0e8fa] text-lais-primary grid place-items-center text-[12px] font-semibold"
              >
                4
              </span>
              <span>
                No momento do envio a imobiliária é notificada, e clientes que
                responderem
                <strong class="text-[#1a1a1a]">
                  entram automaticamente em atendimento
                </strong>
                com a Lais.
              </span>
            </li>
          </ol>
        </LaisCard>
      </div>
    </div>

    <LaisModal
      :show="showModal"
      title="Novo disparo"
      max-width-class="max-w-[560px]"
      @close="fecharNovo"
    >
      <form
        id="novo-disparo-form"
        class="grid gap-4"
        @submit.prevent="salvarDisparo"
      >
        <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <LaisField label="Categoria de template">
            <select
              :value="selectedTemplateCategoryId"
              :class="input"
              @change="selecionarCategoriaTemplate($event.target.value)"
            >
              <option
                v-for="category in ATUALIZACAO_TEMPLATE_CATEGORIES"
                :key="category.id"
                :value="category.id"
              >
                {{ category.label }}
              </option>
            </select>
          </LaisField>
          <LaisField label="Template aprovado" required>
            <select
              :value="novoDisparo.template_id"
              required
              :class="input"
              @change="selecionarTemplate($event.target.value)"
            >
              <option
                v-for="template in templatesForSelectedCategory"
                :key="template.id"
                :value="template.id"
              >
                {{ template.name }} · v{{ template.approvedVersion }}
              </option>
            </select>
          </LaisField>
        </div>

        <div
          v-if="selectedTemplate"
          class="rounded-lais-sm border border-lais-border bg-lais-bg-soft px-4 py-3"
        >
          <div
            class="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between"
          >
            <p class="text-[13px] font-semibold text-lais-text">
              {{ selectedTemplate.name }}
            </p>
            <p class="text-[12px] text-lais-text-muted">
              Versão aprovada {{ selectedTemplate.approvedVersion }}
            </p>
          </div>
          <p class="mt-2 text-[12px] leading-relaxed text-lais-text-body">
            {{ selectedTemplateCategory?.description }}
          </p>
          <div class="mt-3 grid gap-2">
            <p class="text-[12px] font-semibold text-lais-text-body">
              Variáveis permitidas
            </p>
            <div class="flex flex-wrap gap-2">
              <span
                v-for="variable in selectedTemplateVariableDefinitions"
                :key="variable.name"
                class="inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-semibold"
                :class="
                  variable.required
                    ? 'border-lais-primary bg-[#f5eef9] text-lais-primary'
                    : 'border-lais-border bg-white text-lais-text-body'
                "
              >
                {{ variable.token }}
                <span v-if="variable.required">Obrigatória</span>
              </span>
            </div>
          </div>
        </div>

        <LaisField label="Título do disparo" required>
          <input
            v-model="novoDisparo.titulo"
            type="text"
            required
            :class="input"
          />
        </LaisField>
        <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <LaisField label="Canal">
            <select v-model="novoDisparo.canal" :class="input">
              <option value="whatsapp">WhatsApp</option>
              <option value="email">E-mail</option>
              <option value="sms">SMS</option>
            </select>
          </LaisField>
          <LaisField label="Data do disparo">
            <input
              v-model="novoDisparo.agendado_para"
              type="datetime-local"
              :class="input"
            />
          </LaisField>
        </div>
        <div class="grid grid-cols-1 gap-4 md:grid-cols-3">
          <LaisField label="Bairro">
            <input
              v-model="novoDisparo.filtro_bairro"
              type="text"
              :class="input"
            />
          </LaisField>
          <LaisField label="Tipo">
            <input
              v-model="novoDisparo.filtro_tipo"
              type="text"
              :class="input"
            />
          </LaisField>
          <LaisField label="Finalidade">
            <select v-model="novoDisparo.filtro_finalidade" :class="input">
              <option value="">Todas</option>
              <option value="venda">Venda</option>
              <option value="aluguel">Aluguel</option>
            </select>
          </LaisField>
        </div>
        <LaisField
          label="Mensagem"
          helper="A mensagem final deve manter as variáveis obrigatórias do template."
        >
          <textarea v-model="novoDisparo.mensagem" rows="4" :class="input" />
        </LaisField>
        <div
          class="rounded-lais-sm border border-lais-border bg-white px-4 py-3 text-[12px]"
        >
          <p class="font-semibold text-lais-text">
            Preview com dados de exemplo
          </p>
          <p
            class="mt-2 whitespace-pre-line leading-relaxed text-lais-text-body"
          >
            {{ templatePreviewText }}
          </p>
        </div>
        <div
          role="status"
          class="rounded-lais-sm border px-4 py-3 text-[12px] leading-relaxed"
          :class="templateValidationClass"
        >
          <template v-if="templateValidationIssues.length">
            <p
              v-for="issue in templateValidationIssues"
              :key="`${issue.code}-${issue.variable || 'template'}`"
              class="font-semibold"
            >
              {{ issue.message }}
            </p>
          </template>
          <p v-else class="font-semibold">
            Template aprovado e variáveis obrigatórias presentes.
          </p>
        </div>
        <p v-if="previewCount != null" class="text-[12px] text-lais-text-body">
          <strong class="text-lais-primary">{{ previewCount }}</strong> clientes
          podem receber esta atualização.
        </p>
        <div
          role="note"
          class="rounded-lais-sm border border-amber-200 bg-amber-50 px-4 py-3 text-[12px] leading-relaxed text-[#7a4b00]"
        >
          <p class="font-semibold text-[#6f4500]">
            Antes de enviar para revisão
          </p>
          <ul class="mt-2 grid gap-1">
            <li>
              Disparos de remarketing abrem uma nova janela de atendimento e
              podem gerar cobrança adicional.
            </li>
            <li>
              A audiência precisa ficar entre 30 e 60 contatos qualificados.
            </li>
            <li>Cada conta pode ter apenas 1 disparo por dia.</li>
            <li>A solicitação será revisada na data programada.</li>
          </ul>
        </div>
        <div
          role="status"
          class="rounded-lais-sm border px-4 py-3 text-[12px] leading-relaxed"
          :class="audienceRangeClass"
        >
          {{ audienceRangeMessage }}
        </div>
        <div
          role="status"
          class="rounded-lais-sm border px-4 py-3 text-[12px] leading-relaxed"
          :class="
            hasDailyDisparoConflict
              ? 'border-rose-200 bg-rose-50 text-[#b42318]'
              : 'border-[#eee5d6] bg-[#faf8fd] text-lais-text-body'
          "
        >
          {{ dailyLimitMessage }}
        </div>
      </form>

      <template #footer>
        <div class="flex items-center justify-end gap-3">
          <button
            type="button"
            class="h-10 px-4 text-[13px] font-semibold text-lais-text-body hover:text-lais-text"
            @click="fecharNovo"
          >
            Cancelar
          </button>
          <LaisPrimaryButton
            variant="primary"
            form="novo-disparo-form"
            :is-loading="isSaving"
            :disabled="novoDisparoSubmitDisabled"
            type="submit"
          >
            Enviar para revisão
          </LaisPrimaryButton>
        </div>
      </template>
    </LaisModal>

    <LaisConfirmModal
      :show="showCancelModal"
      title="Cancelar disparo"
      :description="cancelDescription"
      confirm-text="Cancelar disparo"
      :is-loading="isCancelling"
      @close="fecharCancelamento"
      @confirm="cancelarDisparo"
    />

    <LaisModal
      :show="isReviewModalOpen"
      :title="reviewModalConfig.title"
      max-width-class="max-w-[520px]"
      @close="fecharReviewModal"
    >
      <div class="grid gap-4">
        <p class="text-[13px] leading-relaxed text-lais-text-body">
          Registre a nota de revisão que ficará salva no disparo e visível para
          a operação.
        </p>
        <LaisField label="Nota de revisão">
          <textarea
            ref="reviewNotesTextarea"
            v-model="reviewNotes"
            rows="5"
            :placeholder="reviewModalConfig.placeholder"
            :class="input"
          />
        </LaisField>
      </div>

      <template #footer>
        <div class="flex items-center justify-end gap-3">
          <button
            type="button"
            class="h-10 px-4 text-[13px] font-semibold text-lais-text-body hover:text-lais-text disabled:cursor-not-allowed disabled:opacity-60"
            :disabled="isReviewModalSubmitting"
            @click="fecharReviewModal"
          >
            Cancelar
          </button>
          <button
            type="button"
            class="inline-flex h-10 items-center gap-2 rounded-lais px-4 text-[13px] font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-60"
            :class="reviewModalConfig.confirmClass"
            :disabled="isReviewModalSubmitting"
            @click="confirmarReviewModal"
          >
            <span
              v-if="isReviewModalSubmitting"
              class="i-lucide-loader-circle size-4 animate-spin"
              aria-hidden="true"
            />
            {{ reviewModalConfig.confirmText }}
          </button>
        </div>
      </template>
    </LaisModal>

    <LaisModal
      :show="showRepublishModal"
      :title="republishTitle"
      max-width-class="max-w-[520px]"
      @close="fecharRepublish"
    >
      <div class="grid gap-4">
        <div
          v-if="republishFeedback"
          class="rounded-lais border p-4"
          :class="
            republishFeedback.kind === 'positive'
              ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
              : 'border-rose-200 bg-rose-50 text-rose-800'
          "
        >
          <p class="text-[14px] font-semibold">
            {{ republishFeedback.title }}
          </p>
          <p
            v-if="republishFeedback.subtitle"
            class="mt-1 text-[13px] leading-relaxed"
          >
            {{ republishFeedback.subtitle }}
          </p>
        </div>

        <template v-else>
          <p class="text-[13px] leading-relaxed text-lais-text-body">
            Você poderá editar as características do anúncio após a
            republicação.
          </p>
          <div
            v-if="selectedListing"
            class="rounded-lais-sm border border-lais-border bg-lais-bg-soft p-4"
          >
            <p class="truncate text-[14px] font-semibold text-lais-text">
              {{ selectedListing.titulo }}
            </p>
            <p class="mt-1 text-[12px] text-lais-text-muted">
              {{ selectedListing.bairro || 'Bairro não informado' }}
              <span v-if="selectedListing.cidade">
                · {{ selectedListing.cidade }}
              </span>
            </p>
          </div>
        </template>
      </div>

      <template #footer>
        <div class="flex items-center justify-end gap-3">
          <button
            type="button"
            class="h-10 px-4 text-[13px] font-semibold text-lais-text-body hover:text-lais-text"
            @click="fecharRepublish"
          >
            Fechar
          </button>
          <LaisPrimaryButton
            v-if="!republishFeedback || republishFeedback.kind === 'critical'"
            variant="primary"
            :is-loading="isReactivatingListing"
            icon="i-lucide-refresh-cw"
            @click="reativarAnuncio"
          >
            Reativar anúncio
          </LaisPrimaryButton>
        </div>
      </template>
    </LaisModal>
  </section>
</template>
